import React from "react";
import { Calculator, X } from "lucide-react";
import {
  DECIMALES_PRESENTACION,
  formatear,
  num,
  patronEfectivo,
  regresionLineal,
  variableDePatron,
} from "../../utils/calibCalculo";

const idDe = (v) => (v && v._id ? String(v._id) : v ? String(v) : "");

const f = (x) => formatear(x, DECIMALES_PRESENTACION);
// Pendiente e intercepto: 6 cifras significativas (a 2 decimales se pierden)
const fRecta = (x) => formatear(x, null);

const NOMBRES_METODO = {
  ninguna: "sin corrección",
  puntual: "corrección puntual",
  interpolacion: "interpolación lineal",
  regresion: "regresión lineal",
};

// Patrones (efectivos, como los usa el motor) de los puntos de la variable
const patronesDeGrupo = (grupo, patrones) => {
  const porId = new Map(patrones.map((p) => [idDe(p._id), p]));
  const { variable } = grupo;
  if (idDe(variable.patron) && idDe(variable.patronVariable)) {
    const doc = porId.get(idDe(variable.patron));
    const vp = doc ? variableDePatron(doc, variable.patronVariable) : null;
    return doc && vp ? [patronEfectivo(doc, vp)] : [];
  }
  const ids = new Set(
    grupo.puntos.flatMap(({ med }) =>
      (med.repeticiones || []).map((r) => idDe(r?.patronId)).filter(Boolean).slice(0, 1),
    ),
  );
  return [...ids].map((id) => porId.get(id)).filter(Boolean);
};

// Tabla de la curva de corrección del patrón (C = −E sobre el valor)
const curvaPatron = (patron) => {
  const pts = (patron.puntos || [])
    .map((p) => ({ valor: num(p.valor), error: num(p.error) }))
    .filter((p) => p.valor !== null && p.error !== null)
    .sort((a, b) => a.valor - b.valor);
  const reg = regresionLineal(
    pts.map((p) => p.valor),
    pts.map((p) => -p.error),
  );
  if (!reg) return { reg: null, filas: [] };
  const filas = pts.map((p) => {
    const correccion = -p.error;
    const curva = reg.m * p.valor + reg.b;
    const dif = correccion - curva;
    return { valor: p.valor, correccion, curva, dif, dif2: dif ** 2 };
  });
  const suma = filas.reduce((a, r) => a + r.dif2, 0);
  return { reg, filas, suma };
};

const Th = ({ children, className = "", ...props }) => (
  <th
    {...props}
    className={`px-2 py-1.5 font-semibold text-center align-middle border border-gray-300 ${className}`}
  >
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-2 py-1 text-right tabular-nums border border-gray-200 ${className}`}>
    {children}
  </td>
);

/**
 * Detalle de solo lectura de los cálculos de una variable: error e
 * incertidumbre por punto (resultados del motor) y la curva de corrección
 * del patrón con su incertidumbre por interpolación.
 */
const DetalleCalculosModal = ({ grupo, patrones = [], onClose }) => {
  if (!grupo) return null;
  const u = grupo.unidad ? ` (${grupo.unidad})` : "";
  const patronesUsados = patronesDeGrupo(grupo, patrones);

  // Vi = indicación del equipo; Vp = valor del patrón antes de corregir
  const filas = grupo.puntos.map(({ med, calc }) => {
    const nominal = num(med.puntoReferencia);
    const inc = calc.incertidumbre || {};
    return {
      key: med._id || `${med.variableId}-${med.puntoIndex}`,
      punto: nominal ?? med.puntoReferencia,
      vp: grupo.ebcGenera ? calc.promedio : grupo.capturaPatron ? calc.promedioPatron : nominal,
      vi: grupo.ebcGenera ? nominal : calc.promedio,
      correccion: calc.correccionPatron,
      vpCorregido: calc.valorReferencia,
      error: calc.errorAbsoluto,
      uRep: inc.uRep,
      uPat: inc.uPat,
      uRes: inc.uRes,
      uInterp: inc.uInterp,
      uc: inc.uc,
      k: inc.k,
      U: inc.U,
    };
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-6xl rounded-t-2xl sm:rounded-2xl shadow-xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 flex flex-wrap items-center gap-x-2 break-words">
              <Calculator size={18} className="text-green-700 shrink-0" />
              Cálculos · {grupo.nombre}
              {u && <span className="text-gray-400 font-normal text-sm">{u}</span>}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Solo lectura. Valores calculados por el motor de calibración con
              las lecturas actuales, mostrados a 2 decimales (los cálculos se
              hacen sin redondear).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 shrink-0 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-4 sm:px-5 py-4 space-y-6">
          {/* Tabla 1: error e incertidumbre por punto */}
          <section>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              {grupo.nombre}
              {u}
            </h4>
            {/* Móvil: una tarjeta por punto; desde md la tabla completa */}
            <div className="md:hidden space-y-3">
              {filas.map((r) => (
                <div key={r.key} className="border rounded-lg overflow-hidden text-xs">
                  <div className="bg-gray-600 text-white px-3 py-1.5 font-semibold">
                    Punto {f(r.punto)}
                  </div>
                  <p className="bg-gray-100 px-3 py-1 font-semibold text-gray-700">
                    CÁLCULO DEL ERROR
                  </p>
                  <dl className="divide-y">
                    {[
                      ["Valor patrón (Vp) promedio", r.vp],
                      ["Valor instrumento (Vi) promedio", r.vi],
                      ["Corrección patrón", r.correccion],
                      ["Valor patrón corregido", r.vpCorregido],
                      ["Error", r.error, true],
                    ].map(([label, v, fuerte]) => (
                      <div key={label} className="flex justify-between gap-3 px-3 py-1">
                        <dt className="text-gray-600">{label}</dt>
                        <dd className={`tabular-nums text-right ${fuerte ? "font-semibold" : ""}`}>{f(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="bg-gray-100 px-3 py-1 font-semibold text-gray-700">
                    CÁLCULO DE LA INCERTIDUMBRE
                  </p>
                  <dl className="divide-y">
                    {[
                      ["Repetibilidad", r.uRep],
                      ["U. patrón", r.uPat],
                      ["Resolución", r.uRes],
                      ["Interpolación", r.uInterp],
                      ["Uc", r.uc],
                      ["k", r.k],
                      ["Uexp", r.U, true],
                    ].map(([label, v, fuerte]) => (
                      <div key={label} className="flex justify-between gap-3 px-3 py-1">
                        <dt className="text-gray-600">{label}</dt>
                        <dd className={`tabular-nums text-right ${fuerte ? "font-semibold" : ""}`}>{f(v)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-600 text-white">
                    <Th className="bg-gray-700" />
                    <Th className="bg-gray-700" colSpan={5}>CÁLCULO DEL ERROR</Th>
                    <Th className="bg-gray-700" colSpan={7}>CÁLCULO DE LA INCERTIDUMBRE</Th>
                  </tr>
                  <tr className="bg-gray-500 text-white">
                    <Th>PUNTO</Th>
                    <Th>VALOR PATRÓN (Vp) PROMEDIO</Th>
                    <Th>VALOR INSTRUMENTO (Vi) PROMEDIO</Th>
                    <Th>CORRECCIÓN PATRÓN</Th>
                    <Th>VALOR PATRÓN CORREGIDO</Th>
                    <Th>ERROR</Th>
                    <Th>REPETIBILIDAD</Th>
                    <Th>U. PATRÓN</Th>
                    <Th>RESOLUCIÓN</Th>
                    <Th>INTERPOLACIÓN</Th>
                    <Th>Uc</Th>
                    <Th>k</Th>
                    <Th>Uexp</Th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((r) => (
                    <tr key={r.key} className="even:bg-gray-50">
                      <Td className="font-medium text-gray-700">{f(r.punto)}</Td>
                      <Td>{f(r.vp)}</Td>
                      <Td>{f(r.vi)}</Td>
                      <Td>{f(r.correccion)}</Td>
                      <Td>{f(r.vpCorregido)}</Td>
                      <Td className="font-semibold">{f(r.error)}</Td>
                      <Td>{f(r.uRep)}</Td>
                      <Td>{f(r.uPat)}</Td>
                      <Td>{f(r.uRes)}</Td>
                      <Td>{f(r.uInterp)}</Td>
                      <Td>{f(r.uc)}</Td>
                      <Td>{f(r.k)}</Td>
                      <Td className="font-semibold">{f(r.U)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
              Vp corregido = Vp + corrección · Error = Vi − Vp corregido ·
              Repetibilidad = s/√n · U. patrón = U/k del certificado en el
              mismo punto nominal (sin importar lo medido) ·
              Resolución = res/√12 · Uc = √(Σ u²) · Uexp = k·Uc
            </p>
          </section>

          {/* Tablas 2 y 3: curva de corrección del patrón */}
          {patronesUsados.length === 0 && (
            <p className="text-xs text-gray-500 italic">
              Sin patrón asignado: no hay curva de corrección que mostrar.
            </p>
          )}
          {patronesUsados.map((patron) => {
            const metodo = patron.metodoCorreccion || "ninguna";
            if (metodo !== "regresion") {
              return (
                <p key={idDe(patron._id)} className="text-xs text-gray-600 bg-gray-50 border rounded px-3 py-2">
                  <strong>{patron.nombre}</strong> usa{" "}
                  {NOMBRES_METODO[metodo] || metodo}: la incertidumbre por
                  interpolación (ajuste de la curva) solo aplica con corrección
                  por regresión lineal.
                </p>
              );
            }
            const { reg, filas: curva, suma } = curvaPatron(patron);
            if (!reg) {
              return (
                <p key={idDe(patron._id)} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <strong>{patron.nombre}</strong>: se requieren al menos 2
                  puntos con error en el certificado para la regresión.
                </p>
              );
            }
            const gl = reg.n - 2;
            return (
              <section key={idDe(patron._id)} className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-800 break-words">
                  Curva de corrección · {patron.nombre}
                  <span className="block sm:inline sm:ml-2 font-normal text-xs text-gray-500">
                    C(x) = m·x + b · m = {fRecta(reg.m)} · b = {fRecta(reg.b)}
                  </span>
                </h4>
                <div className="flex flex-col lg:flex-row gap-4 lg:items-start">
                  <div className="overflow-x-auto max-w-full">
                    <table className="min-w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-gray-500 text-white">
                          <Th>PUNTO</Th>
                          <Th>Corrección</Th>
                          <Th>Corrección por la curva</Th>
                          <Th>Diferencia</Th>
                          <Th>Diferencia²</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {curva.map((r) => (
                          <tr key={r.valor} className="even:bg-gray-50">
                            <Td className="font-medium text-gray-700">{f(r.valor)}</Td>
                            <Td>{f(r.correccion)}</Td>
                            <Td>{f(r.curva)}</Td>
                            <Td>{f(r.dif)}</Td>
                            <Td>{f(r.dif2)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <table className="w-full lg:w-auto text-xs border-collapse">
                    <tbody>
                      <tr>
                        <Td className="text-left text-gray-700">Suma diferencias²</Td>
                        <Td>{f(suma)}</Td>
                      </tr>
                      <tr>
                        <Td className="text-left text-gray-700">Raíz (suma²)</Td>
                        <Td>{f(Math.sqrt(suma))}</Td>
                      </tr>
                      <tr>
                        <Td className="text-left text-gray-700">
                          N° puntos de calibración − puntos de la recta
                        </Td>
                        <Td className="whitespace-nowrap">
                          {reg.n} − 2 = {gl}
                        </Td>
                      </tr>
                      <tr className="bg-green-50">
                        <Td className="text-left font-semibold text-gray-800">
                          (Raíz (suma de diferencias²)) / (N° puntos de
                          calibración − puntos de la recta)
                          <span className="block font-normal text-[11px] text-gray-500">
                            Valor usado como Interpolación en el cálculo de la
                            incertidumbre
                          </span>
                        </Td>
                        <Td className="font-semibold">{gl > 0 ? f(reg.sEst) : "—"}</Td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            );
          })}
        </div>

        <div className="px-4 sm:px-5 py-3 border-t flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto text-sm px-4 py-2 sm:py-1.5 rounded-lg border bg-white hover:bg-gray-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetalleCalculosModal;
